import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach JWT from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("vp_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function setAuthToken(token) {
  if (token) localStorage.setItem("vp_token", token);
  else localStorage.removeItem("vp_token");
}

/**
 * Open a WebSocket connection for real-time messaging.
 * Returns the WebSocket instance; caller owns the lifecycle.
 *
 * Usage:
 *   const ws = openMessagingSocket(userId, token, {
 *     onMessage: (msg) => ...,
 *     onTyping:  (evt) => ...,
 *     onRead:    (evt) => ...,
 *   });
 *   ws.sendTyping(convId, true);
 *   ws.close();
 */
export function openMessagingSocket(userId, token, handlers = {}) {
  const wsBase = BACKEND_URL
    ? BACKEND_URL.replace(/^http/, "ws")
    : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;

  const url = `${wsBase}/api/ws/${userId}?token=${encodeURIComponent(token)}`;
  const ws = new WebSocket(url);

  ws.addEventListener("message", (event) => {
    try {
      const frame = JSON.parse(event.data);
      switch (frame.type) {
        case "message":    handlers.onMessage?.(frame.data); break;
        case "typing":     handlers.onTyping?.(frame.data);  break;
        case "read":       handlers.onRead?.(frame.data);    break;
        case "delivered":  handlers.onDelivered?.(frame.data); break;
        case "presence":   handlers.onPresence?.(frame.data); break;
        default: break;
      }
    } catch (_) {}
  });

  ws.addEventListener("open", () => {
    // Heartbeat every 25s to keep connection alive through proxies
    ws._pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ping", data: {} }));
      }
    }, 25000);
    handlers.onOpen?.();
  });

  ws.addEventListener("close", () => {
    clearInterval(ws._pingInterval);
    handlers.onClose?.();
  });

  ws.addEventListener("error", (err) => handlers.onError?.(err));

  // Convenience methods attached to the socket
  ws.sendTyping = (convId, isTyping) => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "typing", data: { conv_id: convId, is_typing: isTyping } }));
    }
  };

  return ws;
}

// ── Formatting helpers ────────────────────────────────────────────────────────

export function formatINR(amount) {
  if (amount == null) return "—";
  const n = Number(amount);
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(n % 1_00_00_000 === 0 ? 0 : 2)} Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(n % 1_00_000 === 0 ? 0 : 2)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

export function tierBadge(tier) {
  const map = {
    bronze:   { label: "Bronze",            color: "bg-amber-700/10 text-amber-800 border-amber-700/30" },
    silver:   { label: "Silver",            color: "bg-slate-200 text-slate-700 border-slate-300" },
    gold:     { label: "Gold Verified",     color: "bg-[#C5A059]/15 text-[#7a5e1e] border-[#C5A059]/40" },
    platinum: { label: "Platinum Verified", color: "bg-[#0F4C3A]/10 text-[#0F4C3A] border-[#0F4C3A]/30" },
  };
  return map[tier] || map.bronze;
}

export function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");
}
